import { chain, max } from 'lodash';

import * as dto from 'client/dto';
import {
  getStatusOfTargetVaccinationDateWrtToday,
  StartDateDec2020,
  TargetVaccinationDateStatus,
  Today,
} from 'utils/dateTimeHelpers';

import {
  extendDatumLineToEdges,
  fillInDatumLineArray,
} from 'components/_graphs/_common/graphUtils';
import { LineSeriesInterface } from 'components/_graphs/BdLineGraph/BdLineGraph';
import { ProjGraphLineData } from 'components/_graphs/BdProjectionGraph/BdProjectionGraph';

import { ReopeningTrackerTrends } from './components/ReopeningTracker/cell/ReopeningTrackerTrendStatement';
import { ImmunityCapRate } from './components/ReopeningTracker/ReopeningTrackerTableCommon';
import {
  ReopeningTrackerCellType,
  ReopeningTrackerDataSetType,
  ReopeningTrackerTrendSummary,
  VaccineTrackerCellData,
} from './covidDataSuite.models';

export const RTAllowedHistoricalNulls = 16;
export const RTHistoricalDaysToCheck = 21;
export const RTColumnWidth = 'calc(100% / 3)';
export const RTInlineRowBreakpoint = 'always';
export const RTFullWidthCellBreakpoint = 'm';

export const trimNullBeginningOfArray = (series: LineSeriesInterface) => ({
  ...series,
  data: series.data.slice(series.data.findIndex(d => d.y != null)),
});

export const mapReopeningTrackerDataToLinePoint = (
  data: dto.VaccinationDataEntryModel[],
  dataType: ReopeningTrackerDataSetType
): ProjGraphLineData[] => {
  switch (dataType) {
    case 'case-level':
      return data
        .filter(d => d.dailyNewCasesPer100000 != null)
        .map(
          d =>
            ({
              x: new Date(d.date),
              y: d.dailyNewCasesPer100000,
              datum: {
                date: d.date,
                mainRate: d.dailyNewCasesPer100000,
                secondaryRate: d.testPositivityLast14Days,
              },
            } as ProjGraphLineData)
        );
    case 'total-pop':
      return data
        .filter(
          d =>
            d.vaccinationRate != null ||
            d.dailyVaccinationRate != null ||
            d.vaccinationRateBest != null ||
            d.vaccinationRateWorst != null
        )
        .map(d => {
          const yVal = calculateRTTotalImmune(d.vaccinationRate, null, ImmunityCapRate);

          return {
            x: new Date(d.date),
            y: yVal,
            lowY: d.vaccinationRateWorst,
            highY: d.vaccinationRateBest,
            datum: {
              date: d.date,
              mainRate: yVal,
              secondaryRate: d.dailyVaccinationRate,
            },
          } as ProjGraphLineData;
        });
    case 'stringency':
      return data
        .filter(d => d.stringencyIndex != null)
        .map(
          d =>
            ({
              x: new Date(d.date),
              y: d.stringencyIndex,
              datum: {
                date: d.date,
                mainRate: d.stringencyIndex,
                secondaryRate: d.stringencyIndexChange,
              },
            } as ProjGraphLineData)
        );
    case 'high-risk':
    default:
      return data
        .filter(
          d =>
            d.vaccinationRate != null ||
            d.dailyVaccinationRate != null ||
            d.vaccinationRateBest != null ||
            d.vaccinationRateWorst != null
        )
        .map(
          d =>
            ({
              x: new Date(d.date),
              y: d.vaccinationRate,
              lowY: d.vaccinationRateWorst,
              highY: d.vaccinationRateBest,
              datum: {
                date: d.date,
                mainRate: d.vaccinationRate,
                secondaryRate: d.dailyVaccinationRate,
              },
            } as ProjGraphLineData)
        );
  }
};

export function mapToVaccineTrackerCellData(
  data: dto.LocationVaccinationRateModel,
  category?: ReopeningTrackerDataSetType
): VaccineTrackerCellData {
  /** Historical data needs to extend to the start date and Today date
   * This is probably changing when historical milesone is done **not for */
  const historicalData = data?.historicalVaccinationData
    ? fillInDatumLineArray(
        extendDatumLineToEdges(
          mapReopeningTrackerDataToLinePoint(data.historicalVaccinationData, category),
          StartDateDec2020,
          category === 'total-pop' ? Today : null
        )
      )
    : [];

  /** The projection data needs to grab the Today point which is the last point in historical */
  const projectedData =
    data?.isReleasable && data?.predictedVaccinationData?.length && category === 'total-pop'
      ? [
          historicalData[historicalData.length - 1],
          ...mapReopeningTrackerDataToLinePoint(data?.predictedVaccinationData || [], category),
        ]
      : [];

  const milestoneStatus: TargetVaccinationDateStatus =
    data?.isReleasable && projectedData?.length && data?.targetVaccinationRateDate == null
      ? 'more-than-12-months'
      : getStatusOfTargetVaccinationDateWrtToday(
          data?.targetVaccinationRateDate ?? data?.targetVaccinationRateWorstDate
        );

  const summary = category !== 'high-risk' ? getSummaryTrend(category, data?.summary) : null;

  let hasAchievedMilestone = false;

  if (category === 'total-pop') {
    hasAchievedMilestone =
      calculateRTTotalImmune(data?.totalVaccinationRate, null, ImmunityCapRate) >= ImmunityCapRate;
  } else {
    hasAchievedMilestone =
      data?.totalVaccinationRate && data?.totalVaccinationRate >= ImmunityCapRate;
  }

  return {
    data,
    historicalData,
    projectedData,
    isProjectionReliable: data?.isReleasable ?? false,
    graphHasData: !!historicalData?.length || !!projectedData?.length,
    milestoneStatus: milestoneStatus,
    hasAchievedMilestone: hasAchievedMilestone ?? false,
    latestUpdateDate: max(
      data?.historicalVaccinationData
        ?.filter(x => x.vaccinationRate != null)
        ?.map(vd => new Date(vd.date))
    ),
    latestUpdateStringencyDate: max(
      data?.historicalVaccinationData
        ?.filter(x => x.stringencyIndex != null)
        .map(x => new Date(x.date))
    ),
    latestUpdateCasesDate: max(
      data?.historicalVaccinationData
        ?.filter(x => x.dailyNewCasesPer100000 != null)
        ?.map(vd => new Date(vd.date))
    ),
    latestUpdateTestsDate: max(
      data?.historicalVaccinationData
        ?.filter(x => x.testPositivityLast14Days != null)
        ?.map(vd => new Date(vd.date))
    ),
    summary,
  };
}

export function computeMaxYValueOfDailyNewCases(data: dto.LocationVaccinationRateModel) {
  if (!data?.historicalVaccinationData?.length) return 10;

  const maxOfAll = chain(data.historicalVaccinationData)
    .map(vaccinationData => vaccinationData?.dailyNewCasesPer100000 ?? 0)
    .max()
    .value();

  if (!maxOfAll) return 10;

  return Math.ceil(maxOfAll / 10) * 10;
}

export function calculateRTTotalImmune(
  vaccinationRate: number,
  immuneRate?: number,
  milestoneRate?: number
) {
  if (vaccinationRate == null && immuneRate == null) return null;

  const unroundedRate = (vaccinationRate ?? 0) + (immuneRate ?? 0);
  const roundedRate = +(vaccinationRate ?? 0).toFixed(3) + +(immuneRate ?? 0).toFixed(3);

  // We never want to round up to the milestone rate so if the unrounded rate is below it we want to round down by .1%
  return milestoneRate && roundedRate === milestoneRate && unroundedRate < milestoneRate
    ? milestoneRate - 0.001
    : roundedRate;
}

export const getSummaryTrend = (
  cellType: ReopeningTrackerCellType,
  summaryModel: dto.LocationVaccinationSummaryModel
): ReopeningTrackerTrendSummary => {
  if (cellType === 'case-level') {
    return getCasesSummary(summaryModel?.currentCases);
  }
  if (cellType === 'total-pop') {
    return getImmunitySummary(summaryModel?.immunity);
  }

  return getGovernmentStringencySummary(summaryModel?.governmentStringency);
};

export const getIconFromOutlookType = (outlook: dto.CovidOutlookType): ReopeningTrackerTrends =>
  outlook === dto.CovidOutlookType.Unknown || outlook == null
    ? 'unknown'
    : outlook === dto.CovidOutlookType.Poor
    ? 'alert'
    : outlook === dto.CovidOutlookType.HasWarnings
    ? 'warning'
    : 'good';

const getCaseTrendsStatement = (currentCases: dto.LocationVaccinationCasesModel) => {
  if (currentCases == null) return 'There are unknown levels of daily cases.';

  const { caseLevel, caseLevelTrend, underreporting } = currentCases;

  const casesStatement =
    caseLevel === dto.CaseLevelType.High
      ? 'are high'
      : caseLevel === dto.CaseLevelType.Moderate
      ? 'are moderate'
      : 'are low';

  const trendStatement =
    caseLevelTrend === dto.CaseLevelTrendType.Rising
      ? 'and rising'
      : caseLevelTrend === dto.CaseLevelTrendType.Steady
      ? 'and steady'
      : caseLevelTrend === dto.CaseLevelTrendType.Decreasing ||
        caseLevelTrend === dto.CaseLevelTrendType.ConsistentlyDecreasing
      ? 'and decreasing'
      : '';

  const underreportingStatement =
    underreporting === dto.UnderreportingStatementType.Likely
      ? ', and underreporting of cases is likely.'
      : underreporting === dto.UnderreportingStatementType.Unknown
      ? ', and the extent of testing is unknown.'
      : '.';

  if (caseLevel === dto.CaseLevelType.Unknown)
    return `There are unknown levels of daily cases${underreportingStatement}`;

  return `Daily cases ${casesStatement} ${trendStatement}${underreportingStatement}`;
};

const getCasesSummary = (
  currentCases: dto.LocationVaccinationCasesModel
): ReopeningTrackerTrendSummary => {
  const outlookStatement =
    currentCases?.casesOutlook === dto.CovidOutlookType.Poor
      ? 'is poor'
      : currentCases?.casesOutlook === dto.CovidOutlookType.HasWarnings
      ? 'has warnings'
      : currentCases?.casesOutlook === dto.CovidOutlookType.Positive
      ? 'is positive'
      : 'is unknown';

  return {
    type: getIconFromOutlookType(currentCases?.casesOutlook),
    text: `The outlook for cases ${outlookStatement}. ${getCaseTrendsStatement(currentCases)}`,
  };
};

const getImmunitySummary = (
  immunity: dto.LocationVaccinationImmunityModel
): ReopeningTrackerTrendSummary => {
  if (
    immunity == null ||
    immunity.immunityLevel === dto.ImmunityLevelType.Unknown ||
    immunity.immunityOutlook === dto.CovidOutlookType.Unknown
  )
    return {
      type: 'unknown',
      text: 'The proportion of the total population that has been fully vaccinated is unknown.',
    };

  const { immunityLevel, immunityOutlook } = immunity;

  const proportionStatement =
    immunityLevel === dto.ImmunityLevelType.VeryLow
      ? 'very low'
      : immunityLevel === dto.ImmunityLevelType.Low
      ? 'low'
      : immunityLevel === dto.ImmunityLevelType.Moderate
      ? 'moderate'
      : immunityLevel === dto.ImmunityLevelType.High
      ? 'high'
      : 'very high';

  const highRiskCoverageStatement =
    immunity.coverageSufficiency === dto.CoverageSufficiencyType.Insufficient
      ? ' There is insufficient vaccination coverage in the high-risk population.'
      : (immunityLevel === dto.ImmunityLevelType.High ||
          immunityLevel === dto.ImmunityLevelType.VeryHigh) &&
        immunity.coverageSufficiency === dto.CoverageSufficiencyType.Unknown
      ? ' Vaccination coverage of the high-risk population is unknown.'
      : '';

  return {
    type: getIconFromOutlookType(immunityOutlook),
    text: `There is a ${proportionStatement} proportion of the total population that has been fully vaccinated.${highRiskCoverageStatement}`,
  };
};

const getGovernmentStringencySummary = (
  stringency: dto.LocationVaccinationGovernmentStringencyModel
): ReopeningTrackerTrendSummary => {
  switch (stringency?.stringencyChange) {
    case dto.StringencyChangeType.Risen:
      return {
        type: 'risen',
        text: 'Government policy on containment and health has risen over the past 30 days.',
      };
    case dto.StringencyChangeType.RemainedStable:
      return {
        type: 'stable',
        text: 'Government policy on containment and health has remained stable over the past 30 days.',
      };
    case dto.StringencyChangeType.Decreased:
      return {
        type: 'decreased',
        text: 'Government policy on containment and health has decreased over the past 30 days.',
      };
    default:
      return { type: 'unknown', text: 'Changes to stringency in government policy are unknown.' };
  }
};

export const getSummaryTitle = (cellType: ReopeningTrackerCellType) =>
  cellType === 'case-level' ? 'CASES' : cellType === 'total-pop' ? 'IMMUNITY' : 'GOVERNMENT POLICY';
